/**
 * ==========================================
 *  Summary: When Does a Link Become Green?
 * ==========================================
 *
 * A link becomes green when it satisfies one or more of the following conditions:
 *
 * 1️ **Exact URL Match**
 *    - The entered URL (`boxUrl`) exactly matches one of the `<a href="">` links in the HTML content.
 *    -  Checked using: `isExactUrlInHtmlContent(boxUrl, htmlContent)`
 *
 * 2️ **Base URL Match**
 *    - The base URL (protocol + domain + path) matches an existing link, ignoring query parameters.
 *    - Example: `https://example.com/page?ref=123` is considered **the same** as `https://example.com/page`.
 *    - Checked using: `isBaseUrlMatching(boxUrl, htmlContent)`
 *
 * 3️ **Anchor Text Match**
 *    - The given word appears inside an `<a>` tag as anchor text.
 *    -  Checked using: `isWordInAnchorTag(keyword, htmlContent)`
 *
 * 4️ **Valid URL**
 *    - The entered text must be a valid HTTP/HTTPS URL.
 *    -  Checked using: `isValidHttpUrl(link)`
 *
 * ==========================================
 *   Example Scenarios:
 * ==========================================
 *
 * | User Input                | HTML Content                                      | Becomes Green? | Why?                                      |
 * |---------------------------|---------------------------------------------------|---------------|------------------------------------------|
 * | `https://example.com/page?x=1` | `<a href="https://example.com/page">Click here</a>` |  Yes  | Base URL matches (`isBaseUrlMatching`)   |
 * | `https://example.com/blog` | `<a href="https://example.com/blog">Read More</a>` |  Yes  | Exact match (`isExactUrlInHtmlContent`) |
 * | `"Home"`                  | `<a href="https://example.com">Home</a>`         |  Yes  | Anchor text match (`isWordInAnchorTag`) |
 * | `https://www.example.com`  | `<a href="https://example.com">Example</a>`      | Yes  | Normalized URL (`normalizeUrl`) removes "www." |
 *
 * ==========================================
 *   When Does a Link NOT Turn Green?
 * ==========================================
 * - The link is missing from the HTML content.
 *
 * - The keyword is misspelled or not inside an `<a>` tag.
 *
 */

import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { replaceAmpersands } from 'utils/StringUtils';
import {
  isWordInAnchorTag,
  isExactUrlInHtmlContent,
  isBaseUrlMatching,
  isValidHttpUrl,
} from 'helpers/InternalLinksHelper.js';

const InternalLinksList = ({
  websites,
  actualHtmlContent,
  checkIfAllLinesGreen,
  userRole,
}) => {
  const { t } = useTranslation();
  // Function to format the mark color based on matching rules

  const getMarkColor = (word) => {
    const cleanWord = word.replace(/^new page-/, '').trim(); // Clean up the word (remove "new page-" if any)
    const sanitizedWord = replaceAmpersands(cleanWord); // Sanitize the word (replace & with proper character)

    let markColor = { color: 'red', backgroundColor: '#ffc2c2' }; // Default color (invalid link)
    // If it's a valid URL, check URL-based conditions
    if (isValidHttpUrl(sanitizedWord)) {
      if (
        isExactUrlInHtmlContent(sanitizedWord, actualHtmlContent) ||
        isBaseUrlMatching(sanitizedWord, actualHtmlContent) ||
        isWordInAnchorTag(sanitizedWord, actualHtmlContent)
      ) {
        markColor = { color: 'green', backgroundColor: '#2cce6e' }; // Mark valid links as green
      }
    } else {
      // If it's not a URL, only check isWordInAnchorTag
      if (isWordInAnchorTag(sanitizedWord, actualHtmlContent)) {
        markColor = { color: 'green', backgroundColor: '#2cce6e' };
      }
    }

    return { markColor, cleanWord };
  };

  // Split the input websites string into an array of individual words/URLs
  const websitesList = websites
    ? websites
        .split('\n')
        .map((word) => word.trim().replace(/,$/, '')) // Trim and remove trailing commas
        .filter((word) => word.trim() !== '' && word.trim() !== ',') // Remove empty entries
    : [];

  // Check if all marks are green whenever websitesList changes, this is important for text delivery
  useEffect(() => {
    if (userRole !== 'WRITER') return;
    const allGreen =
      !websites || websites?.length === 0
        ? true
        : websitesList.every(
            (word) => getMarkColor(word).markColor.color === 'green'
          );
    checkIfAllLinesGreen(allGreen);
  }, [websitesList, actualHtmlContent, userRole]);

  return (
    <div className="scroll">
      {userRole === 'CLIENT' && (
        <div className="header-title mb-0">
          <>{t('Maillage Interne')}</>
        </div>
      )}
      {(websites?.length === 0 || !websites?.length) && (
        <div>
          <>{t('Inserez le maillage interne')}</>
        </div>
      )}
      {/* Map over the websites list and apply the formatting */}
      <div>
        {websitesList.map((word, index) => {
          // Get the mark color based on matching rules
          const { markColor, cleanWord } = getMarkColor(word);
          return (
            <div key={index}>
              <mark style={markColor}>{cleanWord}</mark>
              <br />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InternalLinksList;
